if (!customElements.get('snl-reference-bands')) {
  customElements.define('snl-reference-bands', class extends HTMLElement {
    connectedCallback() {
      this.abortController = new AbortController();
      const signal = this.abortController.signal;
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.rows = [...this.querySelectorAll('.snlReferences__row')];
      this.button = this.querySelector('.snlReferences__pause');
      this.selectedBlocks = new Set();
      this.userPaused = false;
      this.button?.addEventListener('click', () => {
        this.userPaused = !this.userPaused;
        this.syncPause();
      }, { signal });
      this.motion.addEventListener('change', () => this.rebuild(), { signal });
      this.addEventListener('load', event => {
        if (!event.target.closest('[data-refs-clone]')) this.schedule();
      }, { capture: true, signal });
      this.addEventListener('shopify:block:select', event => {
        this.selectedBlocks.add(event.detail.blockId);
        this.rebuild();
        event.target.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      }, { signal });
      this.addEventListener('shopify:block:deselect', event => {
        this.selectedBlocks.delete(event.detail.blockId);
        this.rebuild();
      }, { signal });
      this.resizeObserver = new ResizeObserver(entries => {
        const width = entries[0].contentRect.width;
        if (width !== this.lastWidth) { this.lastWidth = width; this.schedule(); }
      });
      this.resizeObserver.observe(this);
      this.intersectionObserver = new IntersectionObserver(entries => {
        this.dataset.offscreen = String(!entries[0].isIntersecting);
      });
      this.intersectionObserver.observe(this);
      this.schedule();
    }
    schedule() {
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => this.rebuild());
    }
    clone(node) {
      const copy = node.cloneNode(true);
      copy.dataset.refsClone = '';
      copy.setAttribute('aria-hidden', 'true');
      copy.inert = true;
      for (const element of [copy, ...copy.querySelectorAll('*')]) {
        element.removeAttribute('id');
        element.removeAttribute('data-shopify-editor-block');
        if (element.matches('a, button, [tabindex]')) element.setAttribute('tabindex', '-1');
      }
      return copy;
    }
    rebuild() {
      if (!this.isConnected) return;
      const animate = this.dataset.autoScroll === 'true' && !this.motion.matches && this.selectedBlocks.size === 0;
      let running = false;
      for (const row of this.rows) {
        const viewport = row.querySelector('.snlReferences__viewport');
        const track = row.querySelector('.snlReferences__track');
        viewport.classList.remove('is-running');
        track.querySelectorAll('[data-refs-clone]').forEach(node => node.remove());
        const list = track.querySelector('ul');
        const originals = [...list.children];
        if (!animate || !originals.length || !viewport.clientWidth) continue;
        viewport.classList.add('is-running');
        let repeats = 0;
        while (list.getBoundingClientRect().width < viewport.clientWidth && repeats < 20) {
          originals.forEach(node => list.append(this.clone(node)));
          repeats++;
        }
        const distance = list.getBoundingClientRect().width;
        track.style.setProperty('--refs-distance', `${distance}px`);
        track.style.setProperty('--refs-duration', `${distance / (Number(this.dataset.speed) || 25)}s`);
        track.append(this.clone(list));
        viewport.scrollLeft = 0;
        running = true;
      }
      if (this.button) this.button.hidden = !running;
      this.syncPause();
    }
    syncPause() {
      this.dataset.paused = String(this.userPaused);
      if (!this.button) return;
      this.button.querySelector('[data-pause-text]').textContent = this.userPaused ? this.button.dataset.playLabel : this.button.dataset.pauseLabel;
      this.button.querySelector('[data-pause-icon]').textContent = this.userPaused ? '▷' : 'Ⅱ';
    }
    disconnectedCallback() {
      this.abortController?.abort();
      this.resizeObserver?.disconnect();
      this.intersectionObserver?.disconnect();
      cancelAnimationFrame(this.frame);
    }
  });
}
